import { Controller, useFormContext, z } from '@qred/components-library';
import { ComboboxItem } from '@qred/components-library/dist/lib/next/components/Combobox/Combobox.types';
import { Select } from '@qred/components-library/next';
import { numberFormatter } from '@qred/fe-cp-formatting-helpers';
import { getCurrency } from '@qred/shared-component-library/src/currency';
import { CountryCode } from '@qred/shared-enums';
import React from 'react';
import { useSelector } from 'react-redux';
import { removeWhitespace } from '~/helpers/formatters.helper';
import useTranslate from '~/hooks/useTranslate';
import { RootState } from '~/store';

interface TurnoverRange {
  min: number;
  max?: number;
}

const turnoverRangesNonEuro: TurnoverRange[] = [
  { min: 0, max: 10000 },
  { min: 10001, max: 20000 },
  { min: 20001, max: 30000 },
  { min: 30001, max: 50000 },
  { min: 50001, max: 75000 },
  { min: 75001, max: 125000 },
  { min: 125001, max: 250000 },
  { min: 250001, max: 1000000 },
  { min: 1000001, max: 50000000 },
  { min: 50000000 },
];

const turnoverRangesEuro: TurnoverRange[] = [
  { min: 0, max: 1000 },
  { min: 1001, max: 2000 },
  { min: 2001, max: 3000 },
  { min: 3001, max: 5000 },
  { min: 5001, max: 7500 },
  { min: 7501, max: 12500 },
  { min: 12501, max: 25000 },
  { min: 25001, max: 100000 },
  { min: 100001, max: 5000000 },
  { min: 5000000 },
];

const formatTurnoverOption = (
  turnoverRange: TurnoverRange,
  market: CountryCode
): ComboboxItem => {
  const { min, max } = turnoverRange;
  const formatter = numberFormatter(market);

  const option = max
    ? `${formatter(min)} - ${formatter(max)}`
    : `> ${formatter(min)}`;

  return { label: option, value: removeWhitespace(option) };
};

export const turnoverSchema = z.object({
  turnover: z.string().min(1, { message: 'ValidationErrors.Required' }),
});

type TurnoverForm = z.infer<typeof turnoverSchema>;

export const TurnoverSelect = () => {
  const { control, setValue } = useFormContext<TurnoverForm>();

  const market = useSelector((state: RootState) => state.intl.market);

  const t = useTranslate();

  const turnoverOptions =
    getCurrency(market).unit === '€'
      ? turnoverRangesEuro
      : turnoverRangesNonEuro;

  const formattedTurnoverOptions = turnoverOptions.map((option) =>
    formatTurnoverOption(option, market)
  );

  return (
    <Controller
      control={control}
      name="turnover"
      render={({ field, fieldState }) => (
        <Select
          label={`${t('Onboarding.TurnoverLabel') as string} (${
            getCurrency(market).unit
          })`}
          value={field.value}
          data={formattedTurnoverOptions}
          onChange={field.onChange}
          onBlur={() => {
            if (!field.value) {
              setValue('turnover', '');
            }

            field.onBlur();
          }}
          error={fieldState.error?.message && t(fieldState.error.message)}
          allowDeselect={false}
          hint={t('Onboarding.TurnoverHintMessage') as string}
        />
      )}
    />
  );
};
